/**
 * Intensity Field Branding
 *
 * Metadata for asset intensity denominator fields.
 * Keyed by IntensityDenominator enum (from @g17eco/emissions-config).
 * Used by IntensityTreemapPanel (treemap metrics) and AssetDetailSection (info rows + intensity cards).
 *
 * Adding a new IntensityDenominator enum value will cause a compile error here
 * until the corresponding entry is added to INTENSITY_FIELD_META.
 */

import type { LucideIcon } from 'lucide-react';
import { Ruler, Users, Gauge, Zap, Server, Clock, Car } from 'lucide-react';
import type { IntensityDenominator } from '@g17eco/emissions-config';

export interface IntensityFieldMeta {
  label: string;
  unit: string;
  denominatorLabel: string;
  icon: LucideIcon;
  iconColor: string;
  borderColor: string;
  /** Format the raw numeric value for display in info rows */
  formatValue: (v: number) => string;
  /**
   * Minimum value at which this denominator should produce an intensity card.
   * Defaults to `> 0` (positive). Used by `unitCount` to require `> 1` so
   * single-unit assets don't render a meaningless "per unit" card.
   */
  minValue?: number;
}

/**
 * Standard intensity denominators — one per `IntensityDenominator` value.
 * `satisfies` enforces exhaustive coverage at compile time while keeping plain
 * string keys at runtime (avoids CJS enum initialization issues).
 */
const STANDARD_INTENSITY_FIELD_META = {
  floorAreaM2: {
    label: 'Floor Area', unit: 'kgCO₂e/m²', denominatorLabel: 'm²',
    icon: Ruler, iconColor: 'tw:text-sky-500', borderColor: 'sky-500',
    formatValue: (v: number) => `${v.toLocaleString()} m²`,
  },
  employeeCount: {
    label: 'Employees', unit: 'kgCO₂e/employee', denominatorLabel: 'employees',
    icon: Users, iconColor: 'tw:text-violet-500', borderColor: 'violet-500',
    formatValue: (v: number) => v.toLocaleString(),
  },
  operatingHoursPerYear: {
    label: 'Operating Hours', unit: 'kgCO₂e/hr', denominatorLabel: 'hrs/year',
    icon: Clock, iconColor: 'tw:text-amber-500', borderColor: 'amber-500',
    formatValue: (v: number) => v.toLocaleString(),
  },
  ratedCapacityKw: {
    label: 'Rated Capacity', unit: 'kgCO₂e/kW', denominatorLabel: 'kW',
    icon: Zap, iconColor: 'tw:text-amber-500', borderColor: 'amber-500',
    formatValue: (v: number) => `${v.toLocaleString()} kW`,
  },
  itLoadKw: {
    label: 'IT Load', unit: 'kgCO₂e/kW', denominatorLabel: 'kW',
    icon: Zap, iconColor: 'tw:text-yellow-500', borderColor: 'yellow-500',
    formatValue: (v: number) => `${v.toLocaleString()} kW`,
  },
  rackCount: {
    label: 'Racks', unit: 'kgCO₂e/rack', denominatorLabel: 'racks',
    icon: Server, iconColor: 'tw:text-slate-500', borderColor: 'slate-500',
    formatValue: (v: number) => v.toLocaleString(),
  },
  pue: {
    label: 'PUE', unit: 'kgCO₂e/PUE', denominatorLabel: 'PUE',
    icon: Gauge, iconColor: 'tw:text-purple-500', borderColor: 'purple-500',
    formatValue: (v: number) => v.toFixed(2),
  },
} satisfies Record<IntensityDenominator, IntensityFieldMeta>;

/**
 * Non-template intensity denominators — fields that aren't part of the bulk
 * XLSX template but are still meaningful for asset-level intensity. Today:
 * `unitCount` on Vehicle groups (a Vehicle of N units → kgCO₂e per unit).
 */
const EXTRA_INTENSITY_FIELD_META: Record<string, IntensityFieldMeta> = {
  unitCount: {
    label: 'Unit', unit: 'kgCO₂e/unit', denominatorLabel: 'units',
    icon: Car, iconColor: 'tw:text-rose-500', borderColor: 'rose-500',
    formatValue: (v: number) => `${v.toLocaleString()} units`,
    // Group-only — single-unit assets have no meaningful per-unit ratio.
    minValue: 1,
  },
};

export const INTENSITY_FIELD_META: Record<string, IntensityFieldMeta> = {
  ...STANDARD_INTENSITY_FIELD_META,
  ...EXTRA_INTENSITY_FIELD_META,
};
